import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { MdArrowForward, MdArrowBack, MdCloudUpload, MdFolder } from 'react-icons/md';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';

interface Props {
  onNext: () => void;
  onBack: () => void;
  isCreating?: boolean;
  title: string;
  onTitleChange: (title: string) => void;
  logoFile: File | null;
  onLogoFileChange: (file: File | null) => void;
}

export default function FormSetupTutorialStep3({
  onNext,
  onBack,
  isCreating = false,
  title,
  onTitleChange,
  logoFile,
  onLogoFileChange,
}: Props) {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropAreaRef = useRef<HTMLDivElement>(null);

  // logoFileが変更されたとき、プレビューを更新
  useEffect(() => {
    if (logoFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(logoFile);
    } else {
      setLogoPreview(null);
    }
  }, [logoFile]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 30) {
      onTitleChange(value);
    }
  };

  const handleFileSelect = (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      showErrorToast('5MB以下の画像ファイルをアップロードしてください。');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showErrorToast('画像ファイルをアップロードしてください。');
      return;
    }

    onLogoFileChange(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex h-[684px] flex-col overflow-hidden rounded-b-md bg-white">
      {/* メインコンテンツ部分 */}
      <div
        className="flex flex-1 flex-col items-center overflow-hidden px-x4l py-xl"
        style={{
          backgroundImage:
            'linear-gradient(119.14346351036795deg, var(--semantic/primary_lighten, rgba(237, 248, 255, 1)) 0%, var(--semantic/background/white, rgba(255, 255, 255, 1)) 30%, var(--semantic/background/white, rgba(255, 255, 255, 1)) 80%, var(--primitive/purple/50, rgba(246, 243, 255, 1)) 100%)',
        }}
      >
        <div className="flex w-full flex-col items-center gap-xl">
          {/* タイトル部分 */}
          <div className="flex flex-col items-center gap-xs">
            <h2 className="text-center text-base leading-normal font-bold text-main">
              エントリーフォームのタイトルと、表示する企業ロゴを設定しましょう！
            </h2>
            <p className="text-center text-xs leading-normal font-bold text-sub">
              （設定した内容は、後からいつでも編集することができます。）
            </p>
          </div>

          {/* フォーム部分 */}
          <div className="w-full rounded-md bg-white px-x4l py-lg shadow-[0px_0px_2px_0px_rgba(0,0,0,0.2)]">
            <div className="flex w-full flex-col gap-md">
              {/* タイトル入力 */}
              <div className="flex flex-col gap-xs">
                <label className="text-sm leading-normal font-bold text-main">タイトル</label>
                <div className="flex flex-col gap-xxs">
                  <Input
                    type="text"
                    value={title}
                    onChange={handleTitleChange}
                    className="h-[42px] w-full rounded-sm border border-solid border-border px-sm"
                    maxLength={30}
                  />
                  <p className="text-xs leading-[1.7] font-normal text-sub">
                    30文字以内で入力してください。
                  </p>
                </div>
              </div>

              {/* ロゴアップロード */}
              <div className="flex flex-col gap-xs">
                <label className="text-sm leading-normal font-bold text-main">ロゴ</label>
                <div className="flex flex-col gap-xxs">
                  <div
                    ref={dropAreaRef}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`flex w-full flex-col items-center gap-sm rounded-sm border-2 border-dashed border-border bg-gray p-lg ${
                      isDragging ? 'border-primary' : ''
                    }`}
                  >
                    {logoPreview ? (
                      <div className="relative w-full">
                        <img
                          src={logoPreview}
                          alt="ロゴプレビュー"
                          className="mx-auto max-h-[200px] object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            onLogoFileChange(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                          className="absolute top-0 right-0 rounded bg-black/50 p-1 text-white"
                        >
                          削除
                        </button>
                      </div>
                    ) : (
                      <>
                        <MdCloudUpload className="size-12 text-primary" />
                        <div className="flex flex-col items-center gap-xxs">
                          <p className="text-lg leading-[1.7] font-normal text-main">
                            ここにドラッグアンドドロップ
                          </p>
                          <p className="text-base leading-[1.7] font-normal text-main">または</p>
                        </div>
                        <Button
                          type="button"
                          color="secondary"
                          size="md"
                          onClick={handleSelectFileClick}
                          className="gap-xxs"
                        >
                          <MdFolder className="size-5" />
                          <span>ファイルを選択</span>
                        </Button>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <p className="text-xs leading-[1.7] font-normal text-sub">
                    5MB以下の画像ファイルをアップロードしてください。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* フッター部分 */}
      <div className="flex items-center justify-center gap-md rounded-b-md border-t border-solid border-border bg-white px-lg py-md">
        <Button color="secondary" size="md" onClick={onBack} className="gap-xxs">
          <MdArrowBack className="size-4" />
          <span>戻る</span>
        </Button>
        <Button
          color="primary"
          size="md"
          onClick={onNext}
          disabled={isCreating}
          className="gap-xxs"
        >
          <span>次へ</span>
          <MdArrowForward className="size-4" />
        </Button>
      </div>
    </div>
  );
}
